'use client'

import React from 'react'
import { useLanguage } from '@/lib/i18n/language-provider'
import type { WorkspaceElement, LEDProcessor, LEDPanel, PowerCabinet, PowerPage, WorkflowStep } from '@/types/synoptique'
import { MireDownloadCard } from './MireDownloadCard'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Separator } from '@/components/ui/separator'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Cable,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Cpu,
  Database,
  Download,
  FileImage,
  Grid3X3,
  Monitor,
  Plus,
  Search,
  Eye,
  Zap,
  ExternalLink,
  Archive,
  X,
} from 'lucide-react'

interface CartoucheData {
  agence: string
  client: string
  evenement: string
  lieu: string
  suite: string
  quoi: string
  legende: string
  date: string
  de: string
}

interface DataPage {
  id: string
  workspaceElements?: WorkspaceElement[]
  [key: string]: unknown
}

interface SynoptiqueSidebarProps {
  isLeftPanelOpen: boolean
  setIsLeftPanelOpen: (v: boolean) => void
  readOnly: boolean
  currentStep: WorkflowStep
  isPro: boolean
  isTauri: boolean
  // Processors
  showProcessors: boolean
  searchProcessor: string
  setSearchProcessor: (v: string) => void
  showProcessorSpecs: boolean
  setShowProcessorSpecs: (v: boolean) => void
  sortedBrands: string[]
  processorsByBrand: Record<string, LEDProcessor[]>
  collapsedProcessorBrands: Set<string>
  toggleProcessorBrandCollapse: (brand: string) => void
  // Panels
  showPanels: boolean
  searchPanel: string
  setSearchPanel: (v: string) => void
  showPanelSpecs: boolean
  setShowPanelSpecs: (v: boolean) => void
  sortedPanelBrands: string[]
  panelsByBrand: Record<string, LEDPanel[]>
  collapsedPanelBrands: Set<string>
  togglePanelBrandCollapse: (brand: string) => void
  setIsCustomPanelDialogOpen: (v: boolean) => void
  // Cabinets
  showCabinets: boolean
  searchCabinet: string
  setSearchCabinet: (v: string) => void
  showCabinetSpecs: boolean
  setShowCabinetSpecs: (v: boolean) => void
  sortedCabinetBrands: string[]
  cabinetsByBrand: Record<string, PowerCabinet[]>
  collapsedCabinetBrands: Set<string>
  toggleCabinetBrandCollapse: (brand: string) => void
  setIsCustomCabinetDialogOpen: (v: boolean) => void
  // Drag & drop
  handleDragStart: (type: string, item: unknown, e: React.DragEvent | React.MouseEvent) => void
  setDraggedItem: (v: null) => void
  setDragMousePosition: (v: null) => void
  setIsDraggingFromList: (v: boolean) => void
  isDraggingFromList: boolean
  // Selection
  setSelectedProcessor: (v: LEDProcessor) => void
  setSelectedPanel: (v: LEDPanel) => void
  setSelectedCabinet: (v: PowerCabinet) => void
  setSelectedItemForProperties: (v: { type: string; data: unknown } | null) => void
  // Onboarding
  onboarding: { setDragging: (v: boolean) => void }
  // Cartouche & export
  cartouche: CartoucheData
  setCartouche: (v: CartoucheData) => void
  demoMode: { state: { isDemo: boolean } }
  exportToPDF: () => void
  exportToImage: () => void
  exportToSVG: () => void
  generateReport: () => Promise<void>
  // Mires data (for cabling step)
  dataPages: DataPage[]
  powerPages: PowerPage[]
  backupPorts: Record<string, Record<number, number>>
  projectName: string
  // Project mires (persistent)
  projectMires: any[]
  onDeleteProjectMire: (mireId: string) => void
  // Formatting helpers
  formatResolution: (v: { width: number; height: number }) => string
  formatNumber: (v: number) => string
}

export function SynoptiqueSidebar({
  isLeftPanelOpen,
  setIsLeftPanelOpen,
  readOnly,
  currentStep,
  isPro,
  isTauri,
  showProcessors,
  searchProcessor,
  setSearchProcessor,
  showProcessorSpecs,
  setShowProcessorSpecs,
  sortedBrands,
  processorsByBrand,
  collapsedProcessorBrands,
  toggleProcessorBrandCollapse,
  showPanels,
  searchPanel,
  setSearchPanel,
  showPanelSpecs,
  setShowPanelSpecs,
  sortedPanelBrands,
  panelsByBrand,
  collapsedPanelBrands,
  togglePanelBrandCollapse,
  setIsCustomPanelDialogOpen,
  showCabinets,
  searchCabinet,
  setSearchCabinet,
  showCabinetSpecs,
  setShowCabinetSpecs,
  sortedCabinetBrands,
  cabinetsByBrand,
  collapsedCabinetBrands,
  toggleCabinetBrandCollapse,
  setIsCustomCabinetDialogOpen,
  handleDragStart,
  setDraggedItem,
  setDragMousePosition,
  setIsDraggingFromList,
  isDraggingFromList,
  setSelectedProcessor,
  setSelectedPanel,
  setSelectedCabinet,
  setSelectedItemForProperties,
  onboarding,
  cartouche,
  setCartouche,
  demoMode,
  exportToPDF,
  exportToImage,
  exportToSVG,
  generateReport,
  dataPages,
  powerPages,
  backupPorts,
  projectName,
  projectMires,
  onDeleteProjectMire,
  formatResolution,
  formatNumber,
}: SynoptiqueSidebarProps) {
  const { t } = useLanguage()

  return (
    <>
      {/* Bande de rappel panneau gauche (quand fermé) */}
      {!isLeftPanelOpen && (
        <div
          className="w-8 bg-gradient-to-r from-muted/40 to-muted/20 border-r-2 border-primary/20 hover:border-primary/40 hover:from-muted/60 hover:to-muted/40 cursor-pointer transition-all duration-300 flex flex-col items-center justify-center gap-4 relative group"
          onClick={() => setIsLeftPanelOpen(true)}
          title={t('title.clickToOpenEquipment')}
        >
          {/* Indicateur visuel d'ouverture */}
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary/30 group-hover:bg-primary/60 transition-colors"></div>

          <div className="text-xs text-muted-foreground group-hover:text-foreground font-medium -rotate-90 whitespace-nowrap transition-colors">
            Équipements
          </div>
          <ChevronRight className="h-4 w-4 text-primary/60 group-hover:text-primary animate-pulse" />
          <div className="flex flex-col gap-2">
            <Cpu className="h-3 w-3 text-muted-foreground opacity-60 group-hover:opacity-100 transition-opacity" />
            <Monitor className="h-3 w-3 text-muted-foreground opacity-60 group-hover:opacity-100 transition-opacity" />
            <Cable className="h-3 w-3 text-muted-foreground opacity-60 group-hover:opacity-100 transition-opacity" />
          </div>
        </div>
      )}
      {/* Panel gauche - Équipements */}
      {isLeftPanelOpen && (
        <div className="w-80 border-r bg-card/50 backdrop-blur-sm transition-all duration-300 relative">
          {readOnly && currentStep !== 'export' && (
            <div className="absolute inset-0 z-30 bg-background/60 backdrop-blur-[2px] flex flex-col items-center justify-center pointer-events-auto">
              <div className="flex flex-col items-center gap-2 px-4 text-center">
                <div className="rounded-full bg-muted p-3">
                  <Eye className="h-6 w-6 text-muted-foreground" />
                </div>
                <p className="text-sm font-medium text-muted-foreground">{t('synoptic.share.readOnlyBanner')}</p>
                <p className="text-xs text-muted-foreground/70">{t('synoptic.share.readOnlySidebar')}</p>
              </div>
            </div>
          )}
          <div className="p-3 space-y-4 overflow-y-auto h-full">

            {/* Mires Data - Cabling Step et Data Step */}
            {(currentStep === 'cabling' || currentStep === 'data') && (() => {
              return (
                <Card>
                  <CardHeader className="pb-2 pt-3">
                    <div className="flex items-center gap-2">
                      <Grid3X3 className="h-5 w-5 text-primary" />
                      <CardTitle className="text-lg">{t('synoptic.miresData')}</CardTitle>
                    </div>
                    <CardDescription className="text-xs">{t('synoptic.miresDataDrag')}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2 py-3">
                    {projectMires.map((mire: any) => {
                      const config = mire.config
                      const cols = config?.ledWallMode?.columns
                      const rows = config?.ledWallMode?.rows
                      const totalPanels = cols && rows ? cols * rows : 0
                      const res = config?.totalResolution
                      
                      // Convertir ProjectMire en WorkspaceElement pour drag n drop
                      const workspaceElement: any = {
                        id: mire.id,
                        type: 'image',
                        data: {
                          name: mire.name,
                          mireConfig: config,
                          capturedImage: config?.capturedImage,
                          bubbleConfig: {
                            countPowerConsumption: true
                          }
                        },
                        position: { x: 0, y: 0 },
                        size: { width: (cols * config?.ledWallMode?.panelWidth || 100) / 6, height: (rows * config?.ledWallMode?.panelHeight || 100) / 6 }
                      }
                      
                      return (
                        <div
                          key={mire.id}
                          className="p-2 rounded-lg border transition-colors cursor-move bg-background/50 hover:bg-accent/50 hover:border-primary select-none"
                          style={{ userSelect: 'none', WebkitUserSelect: 'none' } as React.CSSProperties}
                          draggable={!isTauri}
                          onDragStart={!isTauri ? (e) => handleDragStart('image', workspaceElement, e) : undefined}
                          onDragEnd={!isTauri ? () => {
                            setDraggedItem(null)
                            setDragMousePosition(null)
                            setIsDraggingFromList(false)
                          } : undefined}
                          onMouseDown={isTauri ? (e) => {
                            if (e.button === 0) {
                              e.preventDefault()
                              handleDragStart('image', workspaceElement, e)
                            }
                          } : undefined}
                        >
                          <div className="flex items-start gap-2">
                            {config?.capturedImage ? (
                              <img src={config.capturedImage} alt="mire" className="w-12 h-8 object-cover rounded border flex-shrink-0" />
                            ) : (
                              <div className="w-12 h-8 rounded border bg-primary/5 flex items-center justify-center flex-shrink-0">
                                <Grid3X3 className="h-3 w-3 text-muted-foreground" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium truncate">{mire.name}</p>
                              {res && (
                                <p className="text-[10px] text-muted-foreground">{res.width}×{res.height}px</p>
                              )}
                              {cols && rows && (
                                <p className="text-[10px] text-muted-foreground">{cols}×{rows} panneaux</p>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-destructive hover:text-destructive hover:bg-destructive/10 flex-shrink-0"
                              onClick={(e) => {
                                e.stopPropagation()
                                onDeleteProjectMire(mire.id)
                              }}
                              title="Supprimer cette mire du projet"
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      )
                    })}
                  </CardContent>
                </Card>
              )
            })()}

            {/* Processeurs LED - Conditionnel */}
            {showProcessors && (
              <Card data-onboarding="processor-list">
                <CardHeader className="pb-2 pt-3">
                  <div
                    className="flex items-center justify-between gap-2 cursor-pointer hover:bg-muted/50 rounded-md p-1 -m-1 transition-colors"
                    onClick={() => setIsLeftPanelOpen(false)}
                    title={t('synoptic.hideLeftPanel')}
                  >
                    <div className="flex items-center gap-2">
                      <Cpu className="h-5 w-5 text-primary" />
                      <CardTitle className="text-lg">{t('synoptic.ledProcessors')}</CardTitle>
                    </div>
                    <ChevronLeft className="h-4 w-4 text-muted-foreground opacity-60 hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Search className="h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder={t('synoptic.search')}
                      value={searchProcessor}
                      onChange={(e) => setSearchProcessor(e.target.value)}
                      className="h-8"
                    />
                  </div>
                </CardHeader>
                <CardContent className="space-y-2 py-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">{t('synoptic.showSpecs')}</Label>
                    <Switch
                      checked={showProcessorSpecs}
                      onCheckedChange={setShowProcessorSpecs}
                    />
                  </div>

                  {sortedBrands.map((brand) => (
                    <div key={brand} className="space-y-2">
                      <div
                        className="pt-2 pb-1 cursor-pointer hover:bg-muted/50 rounded px-1 -mx-1 transition-colors flex items-center justify-between"
                        onClick={() => toggleProcessorBrandCollapse(brand)}
                      >
                        <h3 className="text-sm font-semibold text-primary/80 border-b flex-1 pb-1">{brand}</h3>
                        {collapsedProcessorBrands.has(brand) ? (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                      {!collapsedProcessorBrands.has(brand) && processorsByBrand[brand].map((processor) => (
                        <div
                          key={processor.id}
                          className="p-3 rounded-lg border bg-background/50 hover:bg-accent/50 transition-colors cursor-grab active:cursor-grabbing select-none"
                          style={{ userSelect: 'none', WebkitUserSelect: 'none' } as React.CSSProperties}
                          draggable={!isTauri}
                          onDragStart={!isTauri ? (e) => handleDragStart('processor', processor, e) : undefined}
                          onDragEnd={!isTauri ? () => {
                            setDraggedItem(null)
                            setDragMousePosition(null)
                            setIsDraggingFromList(false)
                            onboarding.setDragging(false)
                          } : undefined}
                          onMouseDown={isTauri ? (e) => {
                            // Sur Tauri, utiliser mousedown pour démarrer le drag
                            if (e.button === 0) { // Clic gauche uniquement
                              e.preventDefault() // Empêcher la sélection de texte
                              handleDragStart('processor', processor, e)
                            }
                          } : undefined}
                          onClick={() => {
                            // Ne pas sélectionner si on vient de faire un drag
                            if (!isDraggingFromList) {
                              setSelectedProcessor(processor)
                              setSelectedItemForProperties({ type: 'processor', data: processor })
                            }
                          }}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-medium text-sm">{processor.model}</h4>
                              <p className="text-xs text-muted-foreground">{processor.brand}</p>

                              {showProcessorSpecs && (
                                <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                                  <div>{t('synoptic.maxOutput')} {formatResolution(processor.maxOutputResolution)}</div>
                                  <div>{t('synoptic.ports')} {formatNumber(processor.outputPorts)} | {processor.pixelClockMax}</div>
                                  <div>{t('synoptic.totalPixels')} {formatNumber(processor.totalOutputPixels)}</div>
                                  <div>{t('synoptic.conso')} {formatNumber(processor.powerConsumption)} W</div>
                                </div>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {t('synoptic.dragNDrop')}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Dalles LED - Conditionnel */}
            {showPanels && (
              <Card>
                <CardHeader className="pb-2 pt-3">
                  <div
                    className="flex items-center justify-between gap-2 cursor-pointer hover:bg-muted/50 rounded-md p-1 -m-1 transition-colors"
                    onClick={() => setIsLeftPanelOpen(false)}
                    title={t('synoptic.hideLeftPanel')}
                  >
                    <div className="flex items-center gap-2">
                      <Monitor className="h-5 w-5 text-primary" />
                      <CardTitle className="text-lg">{t('synoptic.ledPanels')}</CardTitle>
                    </div>
                    <ChevronLeft className="h-4 w-4 text-muted-foreground opacity-60 hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Search className="h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder={t('synoptic.search')}
                      value={searchPanel}
                      onChange={(e) => setSearchPanel(e.target.value)}
                      className="h-8"
                    />
                  </div>
                </CardHeader>
                <CardContent className="space-y-2 py-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">{t('synoptic.showSpecs')}</Label>
                    <Switch
                      checked={showPanelSpecs}
                      onCheckedChange={setShowPanelSpecs}
                    />
                  </div>

                  {/* Bouton pour créer un panneau personnalisé */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full border-dashed border-2 border-primary/50 hover:border-primary hover:bg-primary/10"
                    onClick={() => setIsCustomPanelDialogOpen(true)}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    {t('synoptic.custom')}
                  </Button>

                  {sortedPanelBrands.map((brand) => {
                    const brandPanels = panelsByBrand[brand]
                    // Grouper par série (panels LWC ont series, les autres n'en ont pas)
                    const seriesMap = new Map<string, LEDPanel[]>()
                    for (const panel of brandPanels) {
                      const key = (panel as LEDPanel & { series?: string }).series || ''
                      if (!seriesMap.has(key)) seriesMap.set(key, [])
                      seriesMap.get(key)!.push(panel)
                    }
                    const seriesKeys = Array.from(seriesMap.keys()).sort()
                    return (
                    <div key={brand} className="space-y-1">
                      <div
                        className="pt-2 pb-1 cursor-pointer hover:bg-muted/50 rounded px-1 -mx-1 transition-colors flex items-center justify-between"
                        onClick={() => togglePanelBrandCollapse(brand)}
                      >
                        <h3 className="text-sm font-semibold text-primary/80 border-b flex-1 pb-1">
                          {brand}
                          <span className="ml-2 text-[10px] font-normal text-muted-foreground">{brandPanels.length}</span>
                        </h3>
                        {collapsedPanelBrands.has(brand) ? (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                      {!collapsedPanelBrands.has(brand) && seriesKeys.map((seriesKey) => {
                        const seriesPanels = seriesMap.get(seriesKey)!
                        const isOutOfProduction = seriesPanels.some((p) => (p as LEDPanel & { seriesStatus?: string }).seriesStatus === 'out_of_production')
                        return (
                          <div key={seriesKey} className="ml-1">
                            {seriesKey && (
                              <div className="flex items-center gap-1 py-1 px-1">
                                <span className="text-[11px] font-medium text-muted-foreground/80 italic">{seriesKey}</span>
                                {isOutOfProduction && (
                                  <span className="flex items-center gap-0.5 text-[9px] text-amber-500/80 bg-amber-500/10 px-1 rounded">
                                    <Archive className="h-2 w-2" />hors prod.
                                  </span>
                                )}
                                <span className="text-[9px] text-muted-foreground/50">{seriesPanels.length}</span>
                              </div>
                            )}
                            {seriesPanels.map((panel) => (
                              <div
                                key={panel.id}
                                className="select-none p-2.5 rounded-lg border transition-colors cursor-move bg-background/50 hover:bg-accent/50 hover:border-primary mb-1"
                                style={{ userSelect: 'none', WebkitUserSelect: 'none' } as React.CSSProperties}
                                draggable={!isTauri}
                                onDragStart={!isTauri ? (e) => handleDragStart('panel', panel, e) : undefined}
                                onDragEnd={!isTauri ? () => {
                                  setDraggedItem(null)
                                  setDragMousePosition(null)
                                  setIsDraggingFromList(false)
                                } : undefined}
                                onMouseDown={isTauri ? (e) => {
                                  if (e.button === 0) {
                                    e.preventDefault()
                                    handleDragStart('panel', panel, e)
                                  }
                                } : undefined}
                                onClick={() => {
                                  if (!isDraggingFromList) {
                                    setSelectedPanel(panel)
                                    setSelectedItemForProperties({ type: 'panel', data: panel })
                                  }
                                }}
                              >
                                <div className="flex items-start justify-between gap-1">
                                  <div className="flex-1 min-w-0">
                                    <h4 className="font-medium text-sm flex items-center gap-1 truncate">
                                      <Monitor className="h-3 w-3 text-primary shrink-0" />
                                      <span className="truncate">{panel.name}</span>
                                    </h4>
                                    {/* Résumé compact toujours visible */}
                                    <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
                                      {panel.pixelPitch > 0 && (
                                        <span className="text-[10px] text-primary/70 font-medium">{panel.pixelPitch}mm</span>
                                      )}
                                      {panel.dimensions.width > 0 && (
                                        <span className="text-[10px] text-muted-foreground">{panel.dimensions.width}×{panel.dimensions.height}mm</span>
                                      )}
                                      {panel.brightness > 0 && (
                                        <span className="text-[10px] text-muted-foreground">{panel.brightness.toLocaleString()} nits</span>
                                      )}
                                    </div>
                                    {showPanelSpecs && (
                                      <div className="mt-1.5 space-y-0.5 text-[10px] text-muted-foreground border-t border-border/50 pt-1">
                                        {panel.resolution.width > 0 && <div><span className="text-foreground/60">Résolution:</span> {panel.resolution.width}×{panel.resolution.height} px</div>}
                                        {(panel as LEDPanel & { panelSize?: string }).panelSize ? (
                                          <div><span className="text-foreground/60">Taille:</span> {(panel as LEDPanel & { panelSize?: string }).panelSize}</div>
                                        ) : panel.dimensions.width > 0 ? (
                                          <div><span className="text-foreground/60">Taille:</span> {panel.dimensions.width}×{panel.dimensions.height}mm</div>
                                        ) : null}
                                        {panel.panelWeight && <div><span className="text-foreground/60">Poids:</span> {panel.panelWeight}</div>}
                                        {panel.powerMax && <div><span className="text-foreground/60">Conso max:</span> {panel.powerMax}</div>}
                                        {panel.powerAvg && <div><span className="text-foreground/60">Conso avg:</span> {panel.powerAvg}</div>}
                                        {panel.ledType && <div><span className="text-foreground/60">LED:</span> {panel.ledType}</div>}
                                        {panel.viewingAngle && <div><span className="text-foreground/60">Angle:</span> {panel.viewingAngle}</div>}
                                        {panel.ledRefreshRate && <div><span className="text-foreground/60">Refresh:</span> {panel.ledRefreshRate}</div>}
                                        {panel.contrastRatio && <div><span className="text-foreground/60">Contraste:</span> {panel.contrastRatio}</div>}
                                        {panel.receivingCard && <div><span className="text-foreground/60">Carte récept.:</span> {panel.receivingCard}</div>}
                                        {panel.maxHangingRows && <div><span className="text-foreground/60">Accrochage max:</span> {panel.maxHangingRows} rangées</div>}
                                        {panel.maxStackingRows && <div><span className="text-foreground/60">Empilement max:</span> {panel.maxStackingRows} rangées</div>}
                                        {panel.certifications && <div><span className="text-foreground/60">Certif.:</span> {panel.certifications}</div>}
                                        {panel.ipRating && <div><span className="text-foreground/60">IP:</span> {panel.ipRating}</div>}
                                        {panel.sourceUrl && (
                                          <a
                                            href={panel.sourceUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-0.5 text-primary/60 hover:text-primary mt-0.5"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <ExternalLink className="h-2.5 w-2.5" />Source LEDWallCentral
                                          </a>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  <div className="text-[9px] text-muted-foreground shrink-0">
                                    Drag
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )
                      })}
                    </div>
                  )
                  })}
                </CardContent>
              </Card>
            )}

            {/* Armoires/Coffrets électriques - Cabling Step */}
            {showCabinets && (
              <Card data-onboarding="cabinet-list">
                <CardHeader className="pb-2 pt-3">
                  <div
                    className="flex items-center justify-between gap-2 cursor-pointer hover:bg-muted/50 rounded-md p-1 -m-1 transition-colors"
                    onClick={() => setIsLeftPanelOpen(false)}
                    title={t('synoptic.hideLeftPanel')}
                  >
                    <div className="flex items-center gap-2">
                      <Database className="h-5 w-5 text-primary" />
                      <CardTitle className="text-lg">{t('synoptic.powerCabinets')}</CardTitle>
                    </div>
                    <ChevronLeft className="h-4 w-4 text-muted-foreground opacity-60 hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Search className="h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder={t('synoptic.search')}
                      value={searchCabinet}
                      onChange={(e) => setSearchCabinet(e.target.value)}
                      className="h-8"
                    />
                  </div>
                </CardHeader>
                <CardContent className="space-y-2 py-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">{t('synoptic.showSpecs')}</Label>
                    <Switch
                      checked={showCabinetSpecs}
                      onCheckedChange={setShowCabinetSpecs}
                    />
                  </div>

                  {/* Bouton pour créer une armoire personnalisée */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full border-dashed border-2 border-primary/50 hover:border-primary hover:bg-primary/10"
                    onClick={() => setIsCustomCabinetDialogOpen(true)}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    {t('synoptic.custom')}
                  </Button>

                  {sortedCabinetBrands.map((brand) => (
                    <div key={brand} className="space-y-2">
                      <div
                        className="pt-2 pb-1 cursor-pointer hover:bg-muted/50 rounded px-1 -mx-1 transition-colors flex items-center justify-between"
                        onClick={() => toggleCabinetBrandCollapse(brand)}
                      >
                        <h3 className="text-sm font-semibold text-primary/80 border-b flex-1 pb-1">{brand}</h3>
                        {collapsedCabinetBrands.has(brand) ? (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                      {!collapsedCabinetBrands.has(brand) && cabinetsByBrand[brand].map((cabinet) => (
                        <div
                          key={cabinet.id}
                          className="p-3 rounded-lg border bg-background/50 hover:bg-accent/50 transition-colors cursor-grab active:cursor-grabbing select-none"
                          style={{ userSelect: 'none', WebkitUserSelect: 'none' } as React.CSSProperties}
                          draggable={!isTauri}
                          onDragStart={!isTauri ? (e) => handleDragStart('cabinet', cabinet, e) : undefined}
                          onDragEnd={!isTauri ? () => {
                            setDraggedItem(null)
                            setDragMousePosition(null)
                            setIsDraggingFromList(false)
                            onboarding.setDragging(false)
                          } : undefined}
                          onMouseDown={isTauri ? (e) => {
                            if (e.button === 0) {
                              e.preventDefault() // Empêcher la sélection de texte
                              handleDragStart('cabinet', cabinet, e)
                            }
                          } : undefined}
                          onClick={() => {
                            if (!isDraggingFromList) {
                              setSelectedCabinet(cabinet)
                              setSelectedItemForProperties({ type: 'cabinet', data: cabinet })
                            }
                          }}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-medium text-sm flex items-center gap-2">
                                <Zap className="h-3 w-3 text-red-500" />
                                {cabinet.name}
                              </h4>
                              <p className="text-xs text-muted-foreground">{cabinet.brand}</p>

                              {showCabinetSpecs && (
                                <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                                  <div className="flex justify-between">
                                    <span>{t('synoptic.maxPower')}</span>
                                    <span className="font-medium">{(cabinet.maxPower / 1000).toFixed(1)} kW</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span>{t('synoptic.outletsCount')}</span>
                                    <span className="font-medium">{cabinet.outlets}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span>{t('synoptic.voltage')}</span>
                                    <span className="font-medium">{cabinet.voltage}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span>{t('synoptic.dimensions')}</span>
                                    <span className="font-medium">{cabinet.dimensions.width}×{cabinet.dimensions.height}mm</span>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground ml-2">
                              Drag & Drop →
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Cartouche d'export - Available only on export step */}
            {currentStep === 'export' && (
              <Card data-onboarding="cartouche">
                <CardHeader className="pb-2 pt-3">
                  <div
                    className="flex items-center justify-between gap-2 cursor-pointer hover:bg-muted/50 rounded-md p-1 -m-1 transition-colors"
                    onClick={() => setIsLeftPanelOpen(false)}
                    title={t("title.clickToHideLeftPanel")}
                  >
                    <div className="flex items-center gap-2">
                      <FileImage className="h-5 w-5 text-primary" />
                      <CardTitle className="text-lg">{t("export.cartouche")}</CardTitle>
                    </div>
                    <ChevronLeft className="h-4 w-4 text-muted-foreground opacity-60 hover:opacity-100 transition-opacity" />
                  </div>
                  <CardDescription className="text-xs">
                    {t("export.cartoucheDescription")}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 py-3">
                  {/* Zone des champs — désactivée en readOnly */}
                  <div className="relative">
                    <div className={readOnly ? 'opacity-80' : ''}>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">{t("export.agencyClient")}</Label>
                          <Input
                            value={cartouche.agence}
                            onChange={(e) => setCartouche({ ...cartouche, agence: e.target.value })}
                            className="h-8 text-xs"
                            placeholder={t("placeholder.agencyName")}
                            readOnly={readOnly}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">{t("export.client")}</Label>
                          <Input
                            value={cartouche.client}
                            onChange={(e) => setCartouche({ ...cartouche, client: e.target.value })}
                            className="h-8 text-xs"
                            placeholder={t("placeholder.clientName")}
                            readOnly={readOnly}
                          />
                        </div>
                      </div>

                      <div className="mt-2">
                        <Label className="text-xs">{t("export.event")}</Label>
                        <Input
                          value={cartouche.evenement}
                          onChange={(e) => setCartouche({ ...cartouche, evenement: e.target.value })}
                          className="h-8 text-xs"
                          placeholder={t("placeholder.eventName")}
                          readOnly={readOnly}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <div>
                          <Label className="text-xs">{t("export.location")}</Label>
                          <Input
                            value={cartouche.lieu}
                            onChange={(e) => setCartouche({ ...cartouche, lieu: e.target.value })}
                            className="h-8 text-xs"
                            placeholder={t("placeholder.location")}
                            readOnly={readOnly}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">{t("export.suite")}</Label>
                          <Input
                            value={cartouche.suite}
                            onChange={(e) => setCartouche({ ...cartouche, suite: e.target.value })}
                            className="h-8 text-xs"
                            placeholder={t("placeholder.suite")}
                            readOnly={readOnly}
                          />
                        </div>
                      </div>

                      <div className="mt-2">
                        <Label className="text-xs">{t("export.what")}</Label>
                        <Input
                          value={cartouche.quoi}
                          onChange={(e) => setCartouche({ ...cartouche, quoi: e.target.value })}
                          className="h-8 text-xs"
                          placeholder={t("placeholder.description")}
                          readOnly={readOnly}
                        />
                      </div>

                      <div className="mt-2">
                        <Label className="text-xs">{t("export.synopticLegend")}</Label>
                        <Input
                          value={cartouche.legende}
                          onChange={(e) => setCartouche({ ...cartouche, legende: e.target.value })}
                          className="h-8 text-xs"
                          placeholder={t("placeholder.legend")}
                          readOnly={readOnly}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <div>
                          <Label className="text-xs">{t("export.date")}</Label>
                          <Input
                            type="date"
                            value={cartouche.date}
                            onChange={(e) => setCartouche({ ...cartouche, date: e.target.value })}
                            className="h-8 text-xs"
                            readOnly={readOnly}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">{t("export.from")}</Label>
                          <Input
                            value={cartouche.de}
                            onChange={(e) => setCartouche({ ...cartouche, de: e.target.value })}
                            className="h-8 text-xs"
                            placeholder={t("placeholder.name")}
                            readOnly={readOnly}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <Separator className="my-3" />

                  <div className="space-y-2" data-onboarding="export-options">
                    {/* PDF and SVG exports hidden in demo mode */}
                    {!(demoMode.state.isDemo && !isPro) && (
                      <Button
                        onClick={exportToPDF}
                        className="w-full"
                        size="sm"
                        variant="default"
                      >
                        <FileImage className="h-4 w-4 mr-2" />
                        {t('synoptic.exportPDF')}
                      </Button>
                    )}

                    <Button
                      onClick={exportToImage}
                      className="w-full"
                      size="sm"
                      variant={demoMode.state.isDemo && !isPro ? "default" : "outline"}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      {demoMode.state.isDemo && !isPro ? t('demo.exportPngWithWatermark') : t('action.exportPNG')}
                    </Button>

                    {!(demoMode.state.isDemo && !isPro) && (
                      <Button
                        onClick={exportToSVG}
                        className="w-full"
                        size="sm"
                        variant="outline"
                      >
                        <FileImage className="h-4 w-4 mr-2" />
                        {t('synoptic.exportSVG')}
                      </Button>
                    )}

                    {!(demoMode.state.isDemo && !isPro) && (
                      <>
                        <Separator className="my-2" />
                        <Button
                          onClick={generateReport}
                          className="w-full"
                          size="sm"
                          variant="outline"
                        >
                          <FileImage className="h-4 w-4 mr-2" />
                          {t('synoptic.generateReport')}
                        </Button>
                        <p className="text-xs text-muted-foreground mt-1">
                          {t('synoptic.generateReportDesc')}
                        </p>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Téléchargement des mires - Available only on export step */}
            {currentStep === 'export' && (
              <MireDownloadCard
                dataPages={dataPages}
                powerPages={powerPages}
                backupPorts={backupPorts}
                projectName={projectName}
                isTauri={isTauri}
                setIsLeftPanelOpen={setIsLeftPanelOpen}
              />
            )}
          </div>
        </div>
      )}
    </>
  )
}
